import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const provider = searchParams.get('provider');
    const statusParam = searchParams.get('status');
    const token = searchParams.get('token');
    const errorMessage = searchParams.get('message');

    if (statusParam === 'error') {
      setStatus('error');
      setMessage(errorMessage || `Authentication with ${provider} failed. Please try again.`);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      return;
    }

    if (statusParam === 'success' && token) {
      try {
        // Store the token and log the user in
        localStorage.setItem('authToken', token);
        
        setStatus('success');
        setMessage(`Successfully authenticated with ${provider}! Redirecting...`);
        
        // Set user as logged in with the token
        // Note: The login function expects email/password, so we'll handle OAuth differently
        // For now, we'll just store the token and let the app handle authentication
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } catch (error) {
        console.error('Error processing OAuth token:', error);
        setStatus('error');
        setMessage('Error processing authentication. Please try again.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } else {
      setStatus('error');
      setMessage('Invalid authentication response. Please try again.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [searchParams, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto w-20 h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mb-6">
            <span className="text-white text-2xl font-bold">V</span>
          </div>
          
          {status === 'loading' && (
            <>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Authenticating...
              </h2>
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">
                Please wait while we complete your authentication.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-green-500 text-6xl mb-4">✅</div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Authentication Successful!
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {message}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-red-500 text-6xl mb-4">❌</div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Authentication Failed
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {message}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Back to Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback; 